import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import MapPanel from "@/components/MapPanel";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seal } from "@/components/Seal";
import { useBranding } from "@/lib/branding";
import Footer from "@/components/Footer";
import { useUiState } from "@/lib/uiState";
import {
  Target, TrendingUp, MailX, UserPlus, CalendarHeart, HandCoins,
  Mail, Users, ArrowRight, Clock, CheckCircle2, Eye, Sparkles, Maximize2, X, Trophy, ChevronDown, Loader2, Cake, MapPinOff,
} from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const ago = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function AgentToolkit({ classYear, onNavigate, onData }) {
  const [d, setD] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [campaignId, setCampaignId] = useState("");
  const [full, setFull] = useState(() => localStorage.getItem("ud_toolkit_expanded") === "1");
  const navigate = useNavigate();
  const { stopImpersonation } = useAuth();
  const setFullPersist = (v) => { localStorage.setItem("ud_toolkit_expanded", v ? "1" : "0"); setFull(v); };
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("ud_toolkit_collapsed") === "1");
  const setCollapsedPersist = (v) => { localStorage.setItem("ud_toolkit_collapsed", v ? "1" : "0"); setCollapsed(v); };

  useEffect(() => {
    const params = new URLSearchParams();
    if (classYear) params.set("class_year", classYear);
    if (campaignId) params.set("campaign_id", campaignId);
    const qs = params.toString();
    api.get(`/agent/toolkit${qs ? `?${qs}` : ""}`).then((r) => { setD(r.data); onData && onData(r.data); }).catch(() => setHidden(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classYear, campaignId]);

  if (hidden || !d) return null;

  const go = (tab) => onNavigate && onNavigate(tab);
  const canNav = true;
  const openAlum = async (id, orderIds, label) => {
    if (!id) return;
    setNavContext(orderIds, window.location.pathname + window.location.search, label);
    navigate(d.mode === "preview" ? `/alumni/${id}` : `/agent/alumni/${id}`);
  };
  const pct = Math.min(100, d.goal.participation);
  const target = d.goal.target;

  const items = [
    { key: "no_email", label: "No email on file", count: d.action_items.no_email, icon: MailX, tab: "class" },
    { key: "unclaimed", label: "Unclaimed profiles", count: d.action_items.unclaimed, icon: UserPlus, tab: "class" },
    { key: "invites", label: "Events need invites", count: d.action_items.events_needing_invites.length, icon: CalendarHeart, tab: "events" },
    { key: "pledges", label: "Unfulfilled pledges", count: d.action_items.unfulfilled_pledges, icon: HandCoins, tab: "fundraising" },
  ].filter((i) => i.count > 0);

  const quick = [
    { label: "Email class", icon: Mail, tab: "email" },
    { label: "New event", icon: CalendarHeart, tab: "events" },
    { label: "Record gift", icon: HandCoins, tab: "fundraising" },
    { label: "Leaderboard", icon: Trophy, tab: "leaderboard" },
    { label: "Update classmates", icon: Users, tab: "class" },
  ];

  const modeBadge = d.mode === "preview" ? { label: "Admin Preview", icon: Eye }
    : d.mode === "emulated" ? { label: "Emulating Agent", icon: Eye } : null;

  return (
    <div data-testid="agent-toolkit" className="rounded-xl overflow-hidden border-2 border-[#C8A24C] shadow-lg mb-8">
      <div className="bg-[#001A4B] text-white px-5 py-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Seal className="h-9 w-9" />
          <div>
            <div className="font-serif text-lg leading-none flex items-center gap-2">Class Agent Toolkit <Sparkles className="h-4 w-4 text-[#C8A24C]" /></div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#C8A24C] mt-1">Class of {d.class_year} · Your special tools</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {modeBadge && (
            <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider bg-[#C8A24C] text-[#001A4B] px-2.5 py-1 rounded-full font-bold">
              <modeBadge.icon className="h-3.5 w-3.5" /> {modeBadge.label}
            </span>
          )}
          <button data-testid="toolkit-collapse" onClick={() => setCollapsedPersist(!collapsed)} title={collapsed ? "Expand toolkit" : "Collapse toolkit"} aria-label={collapsed ? "Expand toolkit" : "Collapse toolkit"}
            className="flex items-center justify-center h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
          </button>
          <button data-testid="toolkit-expand" onClick={() => setFullPersist(true)} title="Full screen details"
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded-full transition-colors">
            <Maximize2 className="h-3.5 w-3.5" /> Expand
          </button>
        </div>
      </div>

      {!collapsed && (<>
      <div className="bg-card p-5 grid gap-5 lg:grid-cols-3">
        {/* Participation goal */}
        <div className="lg:col-span-1">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2 flex-wrap"><Target className="h-3.5 w-3.5" /> Participation
            {d.campaigns && d.campaigns.length > 0 && (
              <select data-testid="toolkit-campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}
                className="normal-case tracking-normal rounded border border-input bg-background px-1.5 py-0.5 text-[11px] max-w-[170px]">
                <option value="">All giving</option>
                {d.campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-serif tracking-tight">{d.goal.participation}%</span>
            <span className="text-sm text-muted-foreground mb-1">of {d.goal.target}% target</span>
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-muted overflow-hidden relative">
            <div className="h-full bg-[#C8A24C]" style={{ width: `${pct}%` }} />
            <div className="absolute top-0 bottom-0 w-0.5 bg-[#001A4B]" style={{ left: `${Math.min(100, target)}%` }} title={`Target ${target}%`} />
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> {d.goal.donors} of {d.goal.alumni} gave</span>
            <span className="font-semibold text-foreground">{money(d.goal.raised)} raised</span>
          </div>
        </div>

        {/* Action items */}
        <div className="lg:col-span-1">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Action Items</div>
          {items.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-4"><CheckCircle2 className="h-5 w-5" /> All caught up — nice work!</div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <button key={it.key} data-testid={`toolkit-action-${it.key}`} onClick={() => go(it.tab)} disabled={!onNavigate}
                  className={`w-full flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-left ${onNavigate ? "hover:border-[#C8A24C] hover:bg-muted/40 transition-colors" : "cursor-default"}`}>
                  <it.icon className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm flex-1">{it.label}</span>
                  <span className="text-sm font-bold text-[#001A4B] bg-[#C8A24C]/25 rounded-full px-2 py-0.5 min-w-[28px] text-center">{it.count}</span>
                  {onNavigate && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="lg:col-span-1">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Recent in Your Class</div>
          {d.recent.length === 0 ? (
            <div className="text-sm text-muted-foreground">No recent updates.</div>
          ) : (
            <ul className="space-y-1.5">
              {d.recent.slice(0, 5).map((r) => (
                <li key={r.id} data-testid={`toolkit-recent-${r.id}`} className="flex items-center gap-2 text-sm">
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${r.claimed ? "bg-emerald-500" : "bg-slate-300"}`} />
                  {canNav
                    ? <button data-testid={`toolkit-recent-link-${r.id}`} onClick={() => openAlum(r.id)} className="font-medium truncate text-left text-primary hover:underline">{r.name}</button>
                    : <span className="font-medium truncate">{r.name}</span>}
                  {r.solicit && <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted rounded px-1 py-0.5 shrink-0">{r.solicit}</span>}
                  <span className="text-muted-foreground truncate">· {r.action}</span>
                  <span className="text-muted-foreground/70 ml-auto shrink-0 text-xs">{ago(r.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {onNavigate && (
        <div className="bg-muted/40 border-t border-border px-5 py-3 flex flex-wrap gap-2">
          {quick.map((q) => (
            <button key={q.label} data-testid={`toolkit-quick-${q.tab}`} onClick={() => go(q.tab)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#001A4B] text-white text-xs font-medium hover:brightness-125 transition-all">
              <q.icon className="h-3.5 w-3.5" /> {q.label}
            </button>
          ))}
        </div>
      )}
      </>)}

      {full && createPortal(<ToolkitDetail classYear={classYear} onClose={() => setFullPersist(false)} />, document.body)}
    </div>
  );
}

// Set the Prev/Next navigation context (subset order) + a return target scoped to that subset,
// so RecordNav walks just this list and Back returns to where the click came from.
const setNavContext = (orderIds, returnPath, label) => {
  try {
    if (orderIds && orderIds.length) {
      localStorage.setItem("ud_directory_order", JSON.stringify(orderIds));
      sessionStorage.setItem("ud_record_return", JSON.stringify({ path: returnPath, ids: orderIds }));
      if (label) sessionStorage.setItem("ud_nav_label", label); else sessionStorage.removeItem("ud_nav_label");
    }
  } catch {}
};

function ListCard({ title, icon: Icon, items, empty, onOpen, onResearchAll, research, statusMap }) {
  const tid = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const [open, setOpen] = useUiState(`ud_tk_card_${tid}`, true);
  const pct = research && research.total > 0 ? Math.round((research.done / research.total) * 100) : 0;
  const allDone = research && research.total > 0 && research.done >= research.total;
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`toolkit-card-toggle-${tid}`} className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-primary shrink-0" /><span className="font-medium truncate">{title}</span>
        </button>
        <span className="ml-auto text-xs font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{items.length}</span>
        {onResearchAll && items.length > 0 && (
          research?.running
            ? <span data-testid="research-all-progress" className="flex items-center gap-1.5 text-xs text-amber-700"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {research.done}/{research.total}</span>
            : allDone
              ? <span data-testid="research-all-ready" className="flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>
              : <button data-testid="research-all-btn" onClick={onResearchAll} className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"><Sparkles className="h-3.5 w-3.5" /> Research all</button>
        )}
        <button type="button" onClick={() => setOpen((o) => !o)} aria-label={open ? "Collapse" : "Expand"} className="text-muted-foreground hover:text-foreground transition-colors">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>
      {open && (<>
      {onResearchAll && research?.total > 0 && (
        <div className="h-1 rounded bg-muted mb-2 overflow-hidden"><div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
      )}
      {items.length === 0
        ? <div className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> {empty}</div>
        : <ul className="text-sm space-y-1 max-h-56 overflow-y-auto">{items.map((it, i) => {
            const st = statusMap ? statusMap[it.id] : undefined;
            const dot = st === "done" ? "bg-emerald-500" : st === "pending" ? "bg-amber-400 animate-pulse" : st === "error" ? "bg-rose-400" : "bg-slate-300";
            const ready = st === "done";
            return (
            <li key={i} className="flex items-center gap-1.5 min-w-0">
              {statusMap && it.id && <span title={ready ? "Research ready" : st === "pending" ? "Researching…" : "Not researched"} className={`h-2 w-2 rounded-full shrink-0 ${dot}`} />}
              {it.id && onOpen
                ? <button data-testid={`toolkit-list-link-${it.id}`} onClick={() => onOpen(it.id, items.filter((x) => x.id).map((x) => x.id), title)} className={`text-left hover:underline truncate ${ready ? "text-emerald-700 font-medium" : "text-primary"}`}>{it.text}</button>
                : <span className="truncate">{it.text}</span>}
              {it.code && <span data-testid="solicit-code" className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground shrink-0 bg-muted rounded px-1.5 py-0.5">{it.code}</span>}
            </li>
          );})}</ul>}
      </>)}
    </div>
  );
}

function ToolkitDetail({ classYear, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const navigate = useNavigate();
  const b = useBranding();
  const { stopImpersonation } = useAuth();
  useEffect(() => {
    setErr(false);
    const p = classYear ? `?class_year=${classYear}` : "";
    api.get(`/agent/toolkit/detail${p}`).then((r) => setD(r.data)).catch(() => setErr(true));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classYear]);

  const [openStat, setOpenStat] = useState(null);
  const [secOpen, setSecOpen] = useUiState("ud_tk_sections", { campaign: true, recent: true });
  const [classMembers, setClassMembers] = useState([]);
  const [batches, setBatches] = useState({ no_email: { running: false, done: 0, total: 0 }, no_address: { running: false, done: 0, total: 0 } });
  const [statusMaps, setStatusMaps] = useState({ no_email: {}, no_address: {} });
  const pollRefs = useRef({});
  const fetchBatchStatus = async (kind) => {
    try {
      const { data } = await api.get(`/agent/research-batch/status?kind=${kind}`);
      const m = {}; (data.items || []).forEach((it) => { m[it.id] = it.status; });
      setStatusMaps((prev) => ({ ...prev, [kind]: m }));
      setBatches((prev) => {
        const running = prev[kind].running && data.done < data.total;
        if (!running && pollRefs.current[kind]) { clearInterval(pollRefs.current[kind]); pollRefs.current[kind] = null; }
        return { ...prev, [kind]: { running, done: data.done, total: data.total } };
      });
    } catch {}
  };
  const startBatch = async (kind) => {
    setBatches((b) => ({ ...b, [kind]: { ...b[kind], running: true } }));
    try { await api.post(`/agent/research-batch?kind=${kind}`); } catch {}
    await fetchBatchStatus(kind);
    if (pollRefs.current[kind]) clearInterval(pollRefs.current[kind]);
    pollRefs.current[kind] = setInterval(() => fetchBatchStatus(kind), 3000);
  };
  useEffect(() => {
    if (d) { fetchBatchStatus("no_email"); fetchBatchStatus("no_address"); }
    return () => { Object.values(pollRefs.current).forEach((t) => t && clearInterval(t)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);
  useEffect(() => {
    const cy = (d && d.class_year) || classYear;
    if (!cy) return;
    api.get(`/experience/classes/${cy}`).then((r) => setClassMembers(r.data.members || [])).catch(() => {});
  }, [d, classYear]);
  const canNav = !!d;
  const openAlum = async (id, orderIds, label) => {
    if (!id) return;
    setOpenStat(null);
    // Scope Prev/Next to the clicked subset + remember where to return + what list this is.
    setNavContext(orderIds, window.location.pathname + window.location.search, label);
    // Stay in the class-agent experience (no admin sidebar); preview = real admin view.
    navigate(d.mode === "preview" ? `/alumni/${id}` : `/agent/alumni/${id}`);
  };

  const totals = d ? [
    { key: "classmates", label: "Classmates", value: d.totals.alumni },
    { key: "claimed", label: "Claimed", value: d.totals.claimed },
    { key: "with_email", label: "With Email", value: d.totals.with_email },
    { key: "events", label: "Events", value: d.totals.events },
    { key: "gifts", label: "Gifts", value: d.totals.gifts },
    { key: "raised", label: "Raised", value: money(d.totals.raised) },
    { key: "pledged_outstanding", label: "Pledges Out", value: money(d.totals.pledged_outstanding) },
  ] : [];

  const linesFor = (key) => {
    const b = (d && d.breakdowns) || {};
    const L = (id, text, code) => ({ id, text, code });
    switch (key) {
      case "classmates": return (b.classmates || []).map((x) => L(x.id, x.name + (x.claimed ? " · claimed" : ""), x.solicit));
      case "claimed": return (b.claimed || []).map((x) => L(x.id, x.name, x.solicit));
      case "with_email": return (b.with_email || []).map((x) => L(x.id, `${x.name} — ${x.email}`, x.solicit));
      case "events": return (b.events || []).map((x) => L(null, `${x.title}${x.date ? ` — ${x.date.slice(0, 10)}` : ""}${x.status ? ` · ${x.status}` : ""}`));
      case "gifts": return (b.gifts || []).map((x) => L(x.id, `${x.name} — ${money(x.amount)}${x.campaign ? ` · ${x.campaign}` : ""}${x.type === "pledge" ? " · pledge" : ""}`, x.solicit));
      case "raised": return (b.raised || []).map((x) => L(x.id, `${x.name} — ${money(x.amount)}${x.campaign ? ` · ${x.campaign}` : ""}`, x.solicit));
      case "pledged_outstanding": return (b.pledged_outstanding || []).map((x) => L(x.id, `${x.name} — ${money(x.amount)}`, x.solicit));
      default: return [];
    }
  };
  const openLabel = totals.find((t) => t.key === openStat)?.label || "";

  const rows = d ? [{ ...d.overall, id: "__all", name: "All giving", goal: null }, ...d.campaigns] : [];

  const quick = [
    { label: "Email class", icon: Mail, tab: "email" },
    { label: "New event", icon: CalendarHeart, tab: "events" },
    { label: "Record gift", icon: HandCoins, tab: "fundraising" },
    { label: "Leaderboard", icon: Trophy, tab: "leaderboard" },
    { label: "Update classmates", icon: Users, tab: "class" },
  ];
  const goTab = (tab) => { onClose(); navigate(`/agent?tab=${tab}`); };
  const showQuick = !!d;

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-300/95 overflow-y-auto" data-testid="toolkit-detail">
      {b._resolved && b.logo_secondary && <img src={b.logo_secondary} alt="" aria-hidden="true" className="pointer-events-none select-none fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[84vh] w-auto object-contain opacity-[0.07] -z-10" />}
      <div className="bg-[#001A4B] text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Seal className="h-9 w-9" />
          <div>
            <div className="font-serif text-lg leading-none">Class Agent Toolkit — Full Detail</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#C8A24C] mt-1">Class of {d?.class_year ?? "…"} · Participation target {d?.target ?? "…"}%</div>
          </div>
        </div>
        <button data-testid="toolkit-detail-close" onClick={onClose} className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md"><X className="h-4 w-4" /> Close</button>
      </div>

      {!d ? <div className="p-10 text-muted-foreground" data-testid="toolkit-detail-status">{err ? "Couldn't load class details. Please close and try again." : "Loading…"}</div> : (
        <div className="max-w-6xl mx-auto p-6 space-y-8">
          {showQuick && (
            <div className="flex flex-wrap gap-2" data-testid="detail-quick-actions">
              {quick.map((q) => {
                const Icon = q.icon;
                return (
                  <button key={q.tab} data-testid={`detail-quick-${q.tab}`} onClick={() => goTab(q.tab)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#001A4B] text-white text-sm font-medium hover:brightness-125 transition-all">
                    <Icon className="h-4 w-4" /> {q.label}
                  </button>
                );
              })}
            </div>
          )}
          {classMembers.length > 0 && <MapPanel alumni={classMembers} title={`Class of ${(d && d.class_year) || classYear} Map`} />}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {totals.map((t) => (
              <button key={t.key} data-testid={`stat-${t.key}`} onClick={() => setOpenStat(t.key)}
                className="text-left bg-card border border-border rounded-lg p-4 hover:border-[#C8A24C] hover:shadow-md transition-all">
                <div className="text-2xl font-serif tracking-tight">{t.value}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{t.label}</div>
              </button>
            ))}
          </div>

          <section>
            <button type="button" data-testid="toolkit-section-toggle-campaign" onClick={() => setSecOpen((s) => ({ ...s, campaign: !s.campaign }))} className="w-full font-serif text-xl tracking-tight mb-3 flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" /> Participation by Campaign
              <ChevronDown className={`ml-auto h-5 w-5 text-muted-foreground transition-transform ${secOpen.campaign ? "" : "-rotate-90"}`} />
            </button>
            {secOpen.campaign && (
            <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono"><tr>
                  <th className="text-left px-4 py-2.5">Campaign</th><th className="text-left px-4 py-2.5">Participation</th>
                  <th className="text-right px-4 py-2.5">Donors</th><th className="text-right px-4 py-2.5">Raised</th>
                  <th className="text-right px-4 py-2.5">Pledges Out</th><th className="text-right px-4 py-2.5">Campaign Goal</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {rows.map((c) => (
                    <tr key={c.id} data-testid={`detail-campaign-${c.id}`} className={c.id === "__all" ? "bg-accent/10 font-medium" : "hover:bg-muted/30"}>
                      <td className="px-4 py-2.5">{c.name}</td>
                      <td className="px-4 py-2.5"><div className="flex items-center gap-2"><div className="w-24 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-[#C8A24C]" style={{ width: `${Math.min(100, c.participation)}%` }} /></div><span className="text-xs">{c.participation}%</span></div></td>
                      <td className="px-4 py-2.5 text-right">{c.donors}</td>
                      <td className="px-4 py-2.5 text-right font-medium">{money(c.raised)}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{money(c.pledged_outstanding)}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{c.goal ? money(c.goal) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </section>

          <section className="grid md:grid-cols-2 gap-6">
            <ListCard title="No email on file" icon={MailX} items={d.action_lists.no_email.map((x) => ({ id: canNav ? x.id : null, text: x.name, code: x.solicit }))} empty="Everyone has an email." onOpen={openAlum} onResearchAll={() => startBatch("no_email")} research={batches.no_email} statusMap={statusMaps.no_email} />
            <ListCard title="No address on file" icon={MapPinOff} items={(d.action_lists.no_address || []).map((x) => ({ id: canNav ? x.id : null, text: x.name, code: x.solicit }))} empty="Everyone has an address." onOpen={openAlum} onResearchAll={() => startBatch("no_address")} research={batches.no_address} statusMap={statusMaps.no_address} />
            <ListCard title="Birthdays this & next month" icon={Cake} items={(d.action_lists.birthdays_this_month || []).map((x) => ({ id: canNav ? x.id : null, text: x.when ? `${x.name} · ${x.when}${x.next_month ? " (next mo.)" : ""}` : x.name, code: x.solicit }))} empty="No birthdays this or next month." onOpen={openAlum} />
            <ListCard title="Unclaimed profiles" icon={UserPlus} items={d.action_lists.unclaimed.map((x) => ({ id: canNav ? x.id : null, text: x.name, code: x.solicit }))} empty="All profiles claimed." onOpen={openAlum} />
            <ListCard title="Unfulfilled pledges" icon={HandCoins} items={d.action_lists.unfulfilled_pledges.map((x) => ({ id: canNav ? x.alumnus_id : null, text: `${x.name} — ${money(x.amount)}`, code: x.solicit }))} empty="No open pledges." onOpen={openAlum} />
            <ListCard title="Events needing invites" icon={CalendarHeart} items={d.action_lists.events_needing_invites.map((x) => ({ id: null, text: x.title }))} empty="All events have invites out." />
          </section>

          <section>
            <button type="button" data-testid="toolkit-section-toggle-recent" onClick={() => setSecOpen((s) => ({ ...s, recent: !s.recent }))} className="w-full font-serif text-xl tracking-tight mb-3 flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" /> Recent Activity
              <ChevronDown className={`ml-auto h-5 w-5 text-muted-foreground transition-transform ${secOpen.recent ? "" : "-rotate-90"}`} />
            </button>
            {secOpen.recent && (
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {d.recent.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className={`h-1.5 w-1.5 rounded-full ${r.claimed ? "bg-emerald-500" : "bg-slate-300"}`} />
                  {canNav
                    ? <button data-testid={`detail-recent-link-${r.id}`} onClick={() => openAlum(r.id, d.recent.map((x) => x.id).filter(Boolean), "Recent activity")} className="font-medium text-left text-primary hover:underline">{r.name}</button>
                    : <span className="font-medium">{r.name}</span>}
                  {r.solicit && <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted rounded px-1.5 py-0.5">{r.solicit}</span>}
                  <span className="text-muted-foreground">· {r.action}</span>
                  <span className="ml-auto text-xs text-muted-foreground/70">{ago(r.at)}</span>
                </div>
              ))}
            </div>
            )}
          </section>
        </div>
      )}
      {openStat && createPortal((
        <div className="fixed inset-0 z-[1100] flex items-start justify-center p-4 bg-slate-900/60 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpenStat(null); }}>
          <div className="bg-card w-full max-w-md rounded-lg shadow-2xl my-10" onMouseDown={(e) => e.stopPropagation()} data-testid="stat-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <div className="font-serif text-lg">{openLabel}</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">{linesFor(openStat).length} record{linesFor(openStat).length === 1 ? "" : "s"}</div>
              </div>
              <button data-testid="stat-modal-close" onClick={() => setOpenStat(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              {linesFor(openStat).length === 0
                ? <div className="text-sm text-muted-foreground">No records.</div>
                : <ul className="text-sm space-y-1.5 max-h-[60vh] overflow-y-auto">{linesFor(openStat).map((it, i) => {
                    const canLink = it.id && canNav;
                    return (
                      <li key={i} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#C8A24C] shrink-0" />
                        {canLink
                          ? <button data-testid={`stat-link-${it.id}`} onClick={() => openAlum(it.id, linesFor(openStat).filter((x) => x.id).map((x) => x.id), openLabel)} className="truncate text-left text-primary hover:underline">{it.text}</button>
                          : <span className="truncate">{it.text}</span>}
                        {it.code && <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0 ml-auto">{it.code}</span>}
                      </li>
                    );
                  })}</ul>}
            </div>
          </div>
        </div>
      ), document.body)}
      {d && <Footer />}
    </div>
  );
}
